import { Activity, CellList, KeyValuePairOfStringAndString, MediaItem } from '../../backend/backendapi';
import { Marker, MarkerColor, MarkerFile, MarkerWithObjects } from './types';
import ActivityStore from '../../../stores/ActivityStore';
import CellStore from '../../../stores/CellStore';
import State from './state';

const propertyKey = 'maintenance.overview2d.markers';

export function readMarkers(media: MediaItem): Marker[] {
  const json = media.properties?.find(e => e.key === propertyKey)?.value ?? '{}';
  const file = JSON.parse(json) as MarkerFile;
  switch (file.version) {
    case 1:
      return file.markers.map((m: any) => ({
        ...m,
        id: String(m.id ?? crypto.randomUUID()),
        nx: Number(m.nx),
        ny: Number(m.ny),
        label: m.label ? String(m.label) : undefined,
        color: isMarkerColor(m.color) ? m.color : 'red',
      }));
    default:
      return [];
  }
}

export function writeMarkers(media: MediaItem, markers: Marker[]): void {
  const file = {
    version: 1,
    markers: markers,
  } as MarkerFile;
  media.properties ??= [];

  const json = JSON.stringify(file);
  const currentValue = media.properties?.find(e => e.key === propertyKey)?.value ?? '{}';
  if (currentValue !== json) {//only write if different, fewer vue updates
    media.properties ??= [];
    const index = media.properties.findIndex(e => e.key === propertyKey);
    if (index >= 0) {
      media.properties.splice(index, 1);
    }
    media.properties.push(KeyValuePairOfStringAndString.fromJS({ key: propertyKey, value: json }));
  }
}

export function isMarkerColor(v: any): v is MarkerColor {
  return v === 'red' || v === 'yellow' || v === 'green';
}

export function colorToCss(c: MarkerColor): string {
  if (c === 'green') return '#2ecc71';
  if (c === 'yellow') return '#f1c40f';
  return '#ff3b30';
}

export function getMarkerColor(m: Marker, activityStore: ActivityStore): string {
  return getActivities(m, activityStore).length > 0
    ? colorToCss('red')
    : colorToCss('green');
}

export function getActivities(m: Marker, activityStore: ActivityStore): Activity[] {
  if (m.cellId !== undefined) {
    return activityStore.activities.filter(e => e.cellId === m.cellId);
  }
  return [];
}

export function getCell(m: Marker, cellStore: CellStore): CellList | null {
  if (m.cellId !== undefined) {
    return cellStore.cellsById.get(m.cellId) ?? null;
  }
  return null;
}

export function getMarkerWithObjects(state: State): MarkerWithObjects[] {
  return state.markers.map(m => ({
    marker: m,
    cell: getCell(m, state.cellStore),
    activities: getActivities(m, state.activityStore),
  }));
}

/**
 * Centrally filters markers based on the current search text and issue toggle status.
 */
export function getFilteredMarkers(state: State): MarkerWithObjects[] {
  const allMarkers = getMarkerWithObjects(state);
  const search = (state.filterText || '').toLowerCase().trim();

  return allMarkers.filter(item => {
    // 1. Filter by Issues Toggle
    if (state.onlyIssues && item.activities.length === 0) {
      return false;
    }

    // 2. Filter by Search Text
    if (search) {
      const label = (item.marker.label || '').toLowerCase();
      const cellName = (item.cell?.name || '').toLowerCase();
      const cellLabel = (item.cell?.label || '').toLowerCase();
      const activityTitle = (item.activities[0]?.title || '').toLowerCase();
      const category = (item.marker.category || '').toLowerCase();

      if (
        !label.includes(search) &&
        !cellName.includes(search) &&
        !cellLabel.includes(search) &&
        !activityTitle.includes(search) &&
        !category.includes(search)
      ) {
        return false;
      }
    }
    return true;
  });
}